import { z } from "zod";
import { VALIDATION_KEYS } from "./auth-errors";
import { CEFR_LEVELS, GENDERS, LANGUAGES, PARTS_OF_SPEECH, QUIZ_KINDS, type Language } from "./types";

export const exampleSchema = z.object({
  text: z.string().min(1),
  translation: z.string().min(1),
});

// ─── Inflection / grammar metadata ───────────────────────
// Kept intentionally permissive — `forms` is a display blob dispatched by
// `pos` on the UI side, so we don't force a discriminated-union here. The
// import pipeline normalizes the human-readable input ("4 формы", "I") into
// the DB enum/int codes later.

export const AGREEMENT_TYPES_INPUT = ["4 формы", "2 формы", "несклоняемое"] as const;
export const AGREEMENT_TYPES_DB = ["FOUR_FORMS", "TWO_FORMS", "INVARIABLE"] as const;
export type AgreementTypeDb = (typeof AGREEMENT_TYPES_DB)[number];

export const CONJUGATIONS_INPUT = ["I", "II", "III", "IV"] as const;
export type ConjugationInput = (typeof CONJUGATIONS_INPUT)[number];

/**
 * Map `"4 формы"` → `"FOUR_FORMS"`, etc. Falsy / unknown → undefined so
 * callers can spread the result into Prisma inputs safely.
 */
export function agreementTypeToDb(input: string | null | undefined): AgreementTypeDb | undefined {
  if (!input) return undefined;
  const idx = (AGREEMENT_TYPES_INPUT as readonly string[]).indexOf(input);
  return idx === -1 ? undefined : AGREEMENT_TYPES_DB[idx];
}

/** `"I"` → 1, `"II"` → 2, …. Returns undefined on bad input. */
export function conjugationToInt(input: string | null | undefined): number | undefined {
  if (!input) return undefined;
  const idx = (CONJUGATIONS_INPUT as readonly string[]).indexOf(input);
  return idx === -1 ? undefined : idx + 1;
}

/** Inverse — 1 → `"I"`, for display. */
export function conjugationToRoman(n: number | null | undefined): string | undefined {
  if (n == null || n < 1 || n > CONJUGATIONS_INPUT.length) return undefined;
  return CONJUGATIONS_INPUT[n - 1];
}

// `forms` shape varies by POS. For transport we just accept any string→string
// object; the UI layer knows how to dispatch on `pos` and read the right
// keys. Validating exhaustively here would reject JSON files on the first
// POS variant we hadn't listed — brittle for no real benefit.
//
// Zod 4 needs a key schema on `z.record`; we still want the record index type
// to be `string` so `forms[key]` isn't typed as possibly undefined.
export const formsSchema = z.record(z.string(), z.string().min(1));
export type Forms = z.infer<typeof formsSchema>;

export const imageSchema = z.union([
  z.object({ keyword: z.string().min(1) }),
  z.object({
    url: z.string().url(),
    attribution: z.string().optional(),
  }),
]);

export const importWordSchema = z.object({
  lemma: z.string().min(1),
  language: z.enum(LANGUAGES).default("RO"),
  // Required on import: homonym handling uses `(lemma, language, pos)` as
  // the identity key. A missing POS makes the record indistinguishable
  // from any other same-lemma entry — better to reject at the boundary
  // than silently create rows that collide later.
  pos: z.enum(PARTS_OF_SPEECH),
  gender: z.enum(GENDERS).optional(),
  notes: z.string().optional(),
  pronunciation: z.string().min(1).optional(),
  plural: z.string().min(1).optional(),
  articulated: z.string().min(1).optional(),
  articulated_plural: z.string().min(1).optional(),
  translations: z.array(z.string().min(1)).default([]),
  synonyms: z.array(z.string().min(1)).default([]),
  examples: z.array(exampleSchema).default([]),
  tags: z.array(z.string().min(1)).default([]),
  image: imageSchema.optional(),
  // English one-liner gloss used as the search query against the on-demand
  // image service (`apps/images` → Pexels). Optional — if omitted at import
  // time, the curator can fill it later via the backfill script. Only
  // meaningful on RO words; RU stubs resolve through their RO partner.
  englishGloss: z.string().min(1).optional(),
  // Inflection-paradigm fields. All optional because their presence depends
  // on `pos` — see SCHEMA.md §2.3–§2.4. The import pipeline translates the
  // human-readable values into DB codes before persisting.
  forms: formsSchema.optional(),
  agreement_type: z.enum(AGREEMENT_TYPES_INPUT).optional(),
  conjugation: z.enum(CONJUGATIONS_INPUT).optional(),
  reflexive: z.boolean().optional(),
  impersonal: z.boolean().optional(),
});

export const importFileSchema = z.object({
  defaultTags: z.array(z.string().min(1)).default([]),
  words: z.array(importWordSchema).min(1),
});

export type ImportWord = z.infer<typeof importWordSchema>;
export type ImportFile = z.infer<typeof importFileSchema>;

export const createWordInputSchema = z.object({
  lemma: z.string().min(1).trim(),
  language: z.enum(LANGUAGES).default("RO"),
  pos: z.enum(PARTS_OF_SPEECH).optional(),
  gender: z.enum(GENDERS).optional(),
  notes: z.string().trim().optional(),
  imageUrl: z.string().url().optional(),
  imageAttribution: z.string().optional(),
  pronunciation: z.string().trim().min(1).optional(),
  plural: z.string().trim().min(1).optional(),
  articulated: z.string().trim().min(1).optional(),
  articulatedPlural: z.string().trim().min(1).optional(),
  translations: z.array(z.string().min(1).trim()).default([]),
  synonyms: z.array(z.string().min(1).trim()).default([]),
  examples: z.array(exampleSchema).default([]),
  tags: z.array(z.string().min(1).trim()).default([]),
  // English gloss for image-search query (Pexels). Carried through from
  // import JSON or set later by the backfill script.
  englishGloss: z.string().trim().min(1).optional(),
  // DB-facing shape (already normalized): integer 1–4, enum codes, boolean.
  // Callers coming from JSON import translate the human values first.
  forms: formsSchema.optional(),
  agreementType: z.enum(AGREEMENT_TYPES_DB).optional(),
  conjugation: z.number().int().min(1).max(4).optional(),
  reflexive: z.boolean().optional(),
  impersonal: z.boolean().optional(),
});

export type CreateWordInput = z.infer<typeof createWordInputSchema>;

export const updateWordInputSchema = z.object({
  lemma: z.string().min(1).trim().optional(),
  pos: z.enum(PARTS_OF_SPEECH).optional().nullable(),
  gender: z.enum(GENDERS).optional().nullable(),
  notes: z.string().trim().optional().nullable(),
  imageUrl: z.string().url().optional().nullable(),
  imageAttribution: z.string().optional().nullable(),
  englishGloss: z.string().trim().min(1).optional().nullable(),
  pronunciation: z.string().trim().min(1).optional().nullable(),
  plural: z.string().trim().min(1).optional().nullable(),
  articulated: z.string().trim().min(1).optional().nullable(),
  articulatedPlural: z.string().trim().min(1).optional().nullable(),
});

export type UpdateWordInput = z.infer<typeof updateWordInputSchema>;

export const WORD_STATES = ["new", "learning", "known"] as const;
export type WordState = (typeof WORD_STATES)[number];

export const wordQuerySchema = z.object({
  q: z.string().trim().optional(),
  language: z.enum(LANGUAGES).optional(),
  state: z.enum(WORD_STATES).optional(),
  // Filter by an exact tag name. Used both for CEFR-level chips (A1/A2/…)
  // and for topical tag search (clicking a #tag on WordDetail).
  tag: z.string().trim().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export type WordQuery = z.infer<typeof wordQuerySchema>;
export type { Language };

/* ─── Grammar import ──────────────────────────────────── */

// Accepts either human-readable form (from JSON imports) or DB form
// (from round-trips through the DB). Transform normalizes to DB form so
// the schema's output is always in the shape `GrammarRule.matchers`
// actually stores. Means `parsed.data.matchers.conjugation` is always
// a number, regardless of whether the input was `1` or `"I"`.
const conjugationMatcherSchema = z
  .union([z.number().int().min(1).max(4), z.enum(CONJUGATIONS_INPUT)])
  .transform((v): number => (typeof v === "number" ? v : (conjugationToInt(v) as number)));

// Same pattern for agreement_type: accepts both the Russian strings used
// in word JSON and the DB enum codes, normalizes to DB codes.
const AGREEMENT_TYPES_DB_SET = new Set<string>(AGREEMENT_TYPES_DB);
const agreementTypeMatcherSchema = z
  .union([z.enum(AGREEMENT_TYPES_DB), z.enum(AGREEMENT_TYPES_INPUT)])
  .transform(
    (v): AgreementTypeDb =>
      AGREEMENT_TYPES_DB_SET.has(v) ? (v as AgreementTypeDb) : (agreementTypeToDb(v) as AgreementTypeDb),
  );

// Structured matcher on `GrammarRule`. Each key is a predicate on `Word`:
// the rule auto-applies to a word iff every non-null key here equals the
// corresponding field on the word. Output shape is in DB form (normalized
// by the transforms above), matching what `Word` actually stores.
export const grammarMatchersSchema = z
  .object({
    pos: z.enum(PARTS_OF_SPEECH).optional(),
    gender: z.enum(GENDERS).optional(),
    conjugation: conjugationMatcherSchema.optional(),
    reflexive: z.boolean().optional(),
    impersonal: z.boolean().optional(),
    agreementType: agreementTypeMatcherSchema.optional(),
  })
  .strict();

export type GrammarMatchers = z.infer<typeof grammarMatchersSchema>;

// URL-safe slug: lowercase alpha, digits, dash. Mirrors Postgres's unique
// index on the column — if the pattern here changes, the DB constraint is
// still the final authority.
export const grammarSlugSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be lowercase alphanumeric with single dashes");

// Inline MCQ example used in the Scenarios session's RULE step. The
// learner reads the rule body, then answers 2-3 of these to convert
// fresh exposure into a one-pass recall beat before moving on.
//
// `prompt` is a short question (e.g. "Какой звук у буквы ș?"); the
// `options` array MUST include the value of `answer` exactly once.
// We don't make this a discriminated union with QuizItem because the
// session uses these locally without going through the quiz tables —
// authors hand-pick these per rule for tight pedagogical control.
export const grammarExampleSchema = z
  .object({
    prompt: z.string().trim().min(1),
    options: z.array(z.string().trim().min(1)).min(2).max(5),
    answer: z.string().trim().min(1),
  })
  .refine((v) => v.options.includes(v.answer), {
    message: "`answer` must be one of `options`",
  });
export type GrammarExample = z.infer<typeof grammarExampleSchema>;

export const grammarImportRuleSchema = z.object({
  title: z.string().min(1).trim(),
  // Optional for back-compat — rules imported before slugs existed have
  // none. Required for any rule that wants to be a link target from
  // `relatedRuleSlugs` on another rule.
  slug: grammarSlugSchema.optional(),
  // Short lead paragraph. Shown under the title on both list + detail.
  // Optional because not every rule needs a secondary line.
  summary: z.string().trim().min(1).optional(),
  body: z.string().min(1),
  tags: z.array(z.string().min(1).trim()).default([]),
  // Optional structured predicate. Rule without matchers does not auto-match
  // any words; it can still be linked via `relatedWordLemmas` or shared tags.
  matchers: grammarMatchersSchema.optional(),
  // Hand-picked lemmas this rule is specifically about — used when no
  // structured predicate fits ("Идиомы с pâine", "Разница между mult и foarte").
  relatedWordLemmas: z.array(z.string().min(1).trim()).default([]),
  // Cross-links to other rules by slug. Unknown slugs are tolerated at
  // read time (silently skipped); the import pipeline emits a warning so
  // typos get caught early.
  relatedRuleSlugs: z.array(grammarSlugSchema).default([]),
  // Soft ordering hint for Level mode's "Up next" suggestion: this rule
  // shouldn't surface as the suggested next-to-learn until the listed
  // prereq rules are `known`. Distinct from `relatedRuleSlugs` because
  // direction matters here ("depends on" is one-way; "related to" is
  // symmetric). Doesn't affect availability — users can always study
  // any rule from the full list.
  prerequisiteSlugs: z.array(grammarSlugSchema).default([]),
  // Inline MCQ examples surfaced by the Scenarios session RULE step.
  // Optional / default empty — absence means "rule is text-only".
  examples: z.array(grammarExampleSchema).default([]),
});

export const grammarImportFileSchema = z.object({
  defaultTags: z.array(z.string().min(1).trim()).default([]),
  rules: z.array(grammarImportRuleSchema).min(1),
});

export type GrammarImportRule = z.infer<typeof grammarImportRuleSchema>;
export type GrammarImportFile = z.infer<typeof grammarImportFileSchema>;

// ─── Quiz import ──────────────────────────────────────────────────────
//
// One JSON file per grammar rule. `ruleSlug` resolves to GrammarRule.id
// at import time; importer rejects the file if the slug doesn't match
// (tutors must import the rule first, then its quizzes). Items are
// idempotent on `slug` — re-importing updates in place.

const quizSlugSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug must be lowercase alphanumeric with single dashes");

// Each quiz item kind has different field requirements. Common fields
// validated up front; per-kind constraints (sourceSentence required iff
// TRANSFORM, exactly one `___` in target sentence) enforced via
// `.superRefine` so we can produce specific error messages per item.
const quizItemBaseSchema = z.object({
  slug: quizSlugSchema,
  // Discriminator. Default CLOZE so older JSON without `kind` still
  // parses correctly.
  kind: z.enum(QUIZ_KINDS).default("CLOZE"),
  // The target sentence with one `___` marker. Validated below.
  sentence: z.string().min(1).trim(),
  // TRANSFORM only. Validated below — null/empty for CLOZE.
  sourceSentence: z.string().trim().min(1).optional(),
  sourceTranslation: z.string().trim().min(1).optional(),
  // At least one acceptable answer. v1 importer accepts length 1; the
  // schema allows multi-answer for future use.
  answers: z.array(z.string().min(1).trim()).min(1),
  // 0–6 distractors. More than 6 makes MCQ unworkable on mobile.
  distractors: z.array(z.string().min(1).trim()).max(6).default([]),
  // RU translation of the FULL sentence (with the answer filled in).
  // Optional: A0 phonetic quizzes use Russian meta-sentences where a
  // translation would just duplicate the sentence and leak the answer.
  // For A1+ quizzes with Romanian sentences, populating this is still
  // strongly recommended (no escape hatch otherwise — see quiz-system.md).
  translation: z.string().trim().min(1).optional(),
  // Free-form pill labels. Tutor decides what's useful per quiz.
  hints: z.array(z.string().min(1).trim()).default([]),
  // Optional bold/italic-only explanation for the post-answer panel.
  explanation: z.string().trim().min(1).optional(),
  // Optional anchor word for WordDetail "see in context" surfacing.
  lemma: z.string().trim().min(1).optional(),
  // Phase 6 — when set, marks this item as part of the named
  // scenario's checkpoint exam (excluded from the regular review
  // queue; surfaced by `GET /scenarios/:slug/checkpoint`).
  scenarioSlug: z.string().trim().min(1).optional(),
  // Phase 6 — full set of rules this item exercises. Primary rule
  // still lives in the file's top-level `ruleSlug`; this captures
  // the *additional* rules a checkpoint item touches so weak-rule
  // feedback after a borderline/fail surfaces every miss.
  rulesTested: z.array(z.string().trim().min(1)).default([]),
});

export const quizImportItemSchema = quizItemBaseSchema.superRefine((item, ctx) => {
  // Exactly one `___` marker in `sentence`.
  const blanks = item.sentence.match(/___/g)?.length ?? 0;
  if (blanks !== 1) {
    ctx.addIssue({
      code: "custom",
      path: ["sentence"],
      message: `sentence must contain exactly one '___' marker (found ${blanks})`,
    });
  }
  // Per-kind shape rules.
  if (item.kind === "CLOZE") {
    if (item.sourceSentence !== undefined || item.sourceTranslation !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceSentence"],
        message: "CLOZE items must not include sourceSentence/sourceTranslation",
      });
    }
  } else {
    // TRANSFORM
    if (!item.sourceSentence) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceSentence"],
        message: "TRANSFORM items require sourceSentence",
      });
    }
    if (!item.sourceTranslation) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceTranslation"],
        message: "TRANSFORM items require sourceTranslation",
      });
    }
  }
});

export const quizImportFileSchema = z.object({
  // The owning grammar rule's slug. Importer resolves to GrammarRule.id;
  // rejects the file if the slug isn't found.
  ruleSlug: grammarSlugSchema,
  items: z.array(quizImportItemSchema).min(1),
});

export type QuizImportItem = z.infer<typeof quizImportItemSchema>;
export type QuizImportFile = z.infer<typeof quizImportFileSchema>;

export const gradeButtonSchema = z.enum(["again", "hard", "good", "easy"]);

// Wall-clock seconds the user spent on the card before submitting.
// Optional so older clients + non-UI callers (tests, admin tooling)
// still work; defaults to 0 on the server. Outer bound (999) is a
// sanity cap — the server applies its own tighter clamp on the stored
// value (5 min).
const secondsSpentSchema = z.number().int().min(0).max(999).optional();

// Discriminated union by targetType. WORD/GRAMMAR carry a manual
// `grade`; QUIZ carries `userAnswer` (server auto-computes the grade
// by comparing against `QuizItem.answers[]`). Splitting per-type means
// each variant validates only its applicable fields — no more silently-
// ignored `direction` on GRAMMAR submissions.

const wordSubmitSchema = z.object({
  targetType: z.literal("WORD"),
  targetId: z.string().min(1),
  // Direction is required for words in practice (the server computes a
  // sensible default if omitted, see `directionFor`), but kept optional
  // here for back-compat with older clients.
  direction: z.enum(["RO_TO_RU", "RU_TO_RO"]).optional(),
  grade: gradeButtonSchema,
  secondsSpent: secondsSpentSchema,
});

const grammarSubmitSchema = z.object({
  targetType: z.literal("GRAMMAR"),
  targetId: z.string().min(1),
  grade: gradeButtonSchema,
  secondsSpent: secondsSpentSchema,
});

const quizSubmitSchema = z.object({
  targetType: z.literal("QUIZ"),
  targetId: z.string().min(1),
  // Literal user input — string typed (type-mode) or option clicked
  // (mcq-mode). Server normalizes (case + diacritics) and compares
  // against `QuizItem.answers[]` to decide grade. Empty string is
  // legal — counts as "skip / I don't know" → again.
  userAnswer: z.string().max(500),
  secondsSpent: secondsSpentSchema,
});

export const reviewSubmitSchema = z.discriminatedUnion("targetType", [
  wordSubmitSchema,
  grammarSubmitSchema,
  quizSubmitSchema,
]);

export type ReviewSubmit = z.infer<typeof reviewSubmitSchema>;

// User asserts they already know the card — bypasses the SM-2 quality grade
// path and jumps straight to a mature state on the server. Intentionally
// shaped like `reviewSubmitSchema` minus `grade` so it slots into the same
// controller patterns (same target types, same optional direction).
export const markKnownSchema = z.object({
  targetType: z.enum(["WORD", "GRAMMAR"]),
  targetId: z.string().min(1),
  direction: z.enum(["RO_TO_RU", "RU_TO_RO"]).optional(),
});

export type MarkKnown = z.infer<typeof markKnownSchema>;

// User-facing schemas use i18n-key strings as Zod messages — the web's
// `<FieldError>` detects the `validation.` prefix and translates via
// `useT()` rather than rendering the key verbatim. Server-side these
// strings flow through unchanged but are never user-visible (the web
// validates first; internal callers don't read the body).
export const loginSchema = z.object({
  email: z.string().email(VALIDATION_KEYS.EMAIL_INVALID),
  password: z.string().min(1, VALIDATION_KEYS.REQUIRED),
});

export type LoginInput = z.infer<typeof loginSchema>;

// Sign-up: stricter than login on the password (length floor) plus a
// cross-field confirm check. NIST 800-63B says length beats complexity,
// so we require 8+ chars and don't enforce mixed-case / numbers /
// symbols — those rules drive users to predictable substitutions
// without much real-world entropy gain. Cap at 200 to keep bcrypt
// happy (it truncates at 72 anyway, but a 200-char field is also a
// signal of paste-mistakes / abuse).
export const signUpSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(VALIDATION_KEYS.EMAIL_INVALID),
    // Display name. `z.preprocess` collapses missing/null/whitespace
    // into "" BEFORE the string validator, so:
    //   - server-side callers that omit `name` entirely (legacy API
    //     contract, e2e tests) pass through with an empty value;
    //   - tanstack-form passes a guaranteed `string` (default "");
    //   - Zod's input type stays `string` (no `name?:`), keeping
    //     tanstack-form's strict typing happy.
    // The output transform then maps empty → undefined so the
    // controller forwards a missing field; the server stores null
    // and Home falls back to the email prefix.
    name: z.preprocess(
      (v) => (typeof v === "string" ? v : ""),
      z
        .string()
        .trim()
        .max(80, VALIDATION_KEYS.NAME_TOO_LONG)
        .transform((v) => (v.length > 0 ? v : undefined)),
    ),
    password: z.string().min(8, VALIDATION_KEYS.PASSWORD_TOO_SHORT).max(200, VALIDATION_KEYS.PASSWORD_TOO_LONG),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: VALIDATION_KEYS.PASSWORDS_MUST_MATCH,
    // Attached to `confirmPassword` so the error renders under the
    // confirm field, not the original password field. Matches usual
    // form-UX expectations.
    path: ["confirmPassword"],
  });

export type SignUpInput = z.infer<typeof signUpSchema>;

// Frontend-only mirror of `signUpSchema` whose `name` field is
// strict-required (no preprocess, no optional). Tanstack-form's
// validator wiring is type-checked against the form's `defaultValues`
// shape — having `name?: string` on the API schema breaks that
// alignment when the form defaults to `name: ""`. Splitting the two
// validators keeps the API contract permissive (legacy clients +
// server tests can omit `name`) while the form gets a precise
// `name: string` shape. They share the same cross-field
// confirm-password check.
export const signUpFormSchema = z
  .object({
    name: z.string().trim().max(80, VALIDATION_KEYS.NAME_TOO_LONG),
    email: z.string().trim().toLowerCase().email(VALIDATION_KEYS.EMAIL_INVALID),
    password: z.string().min(8, VALIDATION_KEYS.PASSWORD_TOO_SHORT).max(200, VALIDATION_KEYS.PASSWORD_TOO_LONG),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: VALIDATION_KEYS.PASSWORDS_MUST_MATCH,
    path: ["confirmPassword"],
  });

// Step 1 of the "forgot my password" flow. Just an email address;
// server always responds 200 (anti-enumeration — see
// `email-scaffold.md`). Same trim+lowercase normalization as signup so
// "  USER@x.COM " on the form maps to the same row as the existing
// "user@x.com" row.
export const forgotPasswordSchema = z.object({
  email: z.string().trim().toLowerCase().email(VALIDATION_KEYS.EMAIL_INVALID),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

// Step 2 of the "forgot my password" flow. The plain token comes from
// the email link; password follows the same rules as signup (8–200,
// confirm matches). Token shape is constrained to the `crypto.randomBytes(32)
// .toString("hex")` output — 64 lowercase hex chars — so a malformed
// link rejects at the boundary instead of doing a fruitless DB lookup.
export const resetPasswordSchema = z
  .object({
    token: z.string().regex(/^[a-f0-9]{64}$/, VALIDATION_KEYS.TOKEN_INVALID),
    password: z.string().min(8, VALIDATION_KEYS.PASSWORD_TOO_SHORT).max(200, VALIDATION_KEYS.PASSWORD_TOO_LONG),
    confirmPassword: z.string(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: VALIDATION_KEYS.PASSWORDS_MUST_MATCH,
    path: ["confirmPassword"],
  });

export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// "Confirm my email" — Step 2 of the verification flow. Same 64-hex
// regex as the reset token (both come from `crypto.randomBytes(32)
// .toString("hex")` so the shape is identical) — a malformed token
// rejects at the Zod boundary instead of doing a fruitless DB
// lookup. There is no Step-1 schema: the "request" endpoint is
// auth-gated and takes no body (the user id is read from the JWT).
export const verifyEmailSchema = z.object({
  token: z.string().regex(/^[a-f0-9]{64}$/, VALIDATION_KEYS.TOKEN_INVALID),
});

export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

// ─── Scenario import (Phase 1) ────────────────────────────────────────
//
// One JSON file per scenario. Identity is `slug` — author-stable across
// re-imports. Soft refs to vocab (`wordLemmas`) and grammar (`ruleSlugs`)
// are validated post-parse by the importer; the schema itself only
// guarantees shape and per-string format. See `scenarios.md` §7 for the
// concept and `scenarios-phase1.md` for the implementation contract.

const scenarioSlugSchema = z
  .string()
  .trim()
  .min(1)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "scenario slug must be lowercase alphanumeric with single dashes");

// Plain-string lemma. We don't enforce the lexical pattern here because
// Romanian lemmas can include diacritics, spaces (multi-word verbs like
// "a se duce"), and apostrophes. The importer's soft-ref validator
// surfaces typos as warnings against the actual Word table.
const lemmaRefSchema = z.string().trim().min(1);

const scenarioConstructionSchema = z.object({
  ro: z.string().trim().min(1),
  ru: z.string().trim().min(1),
});

// `completionCriteria.sessionStepsCompleted` accepts either the literal
// "all" (every Session step must be done) or a number (at least N
// steps). Phase 1 only authors `"all"`; the numeric variant is reserved
// for future "core steps required, optional ones can be skipped"
// scenarios — keeping the schema permissive avoids a v2 migration.
const completionCriteriaSchema = z.object({
  sessionStepsCompleted: z.union([z.literal("all"), z.number().int().nonnegative()]),
  checkpointPassed: z.boolean(),
});

export const scenarioImportFileSchema = z.object({
  slug: scenarioSlugSchema,
  title: z.string().trim().min(1),
  level: z.enum(CEFR_LEVELS),
  // Position in the 20-stage grammar map. Positive integer.
  stage: z.number().int().positive(),
  capstone: z.boolean().default(false),
  // The "after this you'll be able to..." sentence. Cap at 200 chars
  // to leave headroom; UI clamps display to ~120.
  promise: z.string().trim().min(10).max(200),
  estimatedMinutes: z.number().int().positive(),

  // At least one word and one rule — a scenario with neither has nothing
  // to teach. Phase 2's foundational A0 track may relax this for grammar-
  // -only scenarios; Phase 1 authors all keep it.
  wordLemmas: z.array(lemmaRefSchema).min(1),
  ruleSlugs: z.array(scenarioSlugSchema).min(1),
  // 1+ constructions — even foundational scenarios surface at least one
  // "say it like this" pattern.
  constructions: z.array(scenarioConstructionSchema).min(1),

  // Soft ref to a future Phase 6 checkpoint quiz. May be null if the
  // checkpoint isn't authored yet — the importer logs but doesn't fail.
  checkpointQuizSlug: scenarioSlugSchema.nullable().optional(),

  completionCriteria: completionCriteriaSchema,

  // Other scenarios that should be `COMPLETED` before this one becomes
  // `AVAILABLE`. Forward refs are tolerated by the Phase 1 importer
  // (warn-only); Phase 2 turns them into errors after the full batch
  // is authored.
  prerequisiteScenarios: z.array(scenarioSlugSchema).default([]),
  tags: z.array(z.string().trim().min(1)).default([]),
});

export type ScenarioImportFile = z.infer<typeof scenarioImportFileSchema>;
export type ScenarioConstructionInput = z.infer<typeof scenarioConstructionSchema>;
